import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useChat, type UseChatHelpers } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import type { UIMessage } from 'ai'
import i18n from 'i18next'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type SubmitLlmChatMessageFeedbackPayload,
  type LlmChatMessageFeedbackDto,
  invalidateLlmChatSessionsList,
  llmChatRowsToUiMessages,
  patchLlmChatSessionInListCache,
  submitLlmChatMessageFeedbackApi,
  useLlmChatProviderInfo,
  type GetLlmChatSessionResponse,
} from '@/shared/hooks/api/llmChat'
import { fetchLegislationDocumentByCelex } from '@/shared/hooks/api/radarScopeTier'
import {
  invalidateProductRecommendationQueries,
  patchRecommendationStatusInCache,
  setProductRecommendationsCache,
  type ProductRecommendation,
} from '@/shared/hooks/api/product'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useLlmChatSessionScopeLabel } from '../hooks/useLlmChatSessionScopeLabel'
import { useSessionLinkedProductStatus } from '../hooks/useSessionLinkedProductStatus'
import { getFeedbackEligibleAssistantMessageIds } from '../llmChatFeedbackEligibility'
import { llmChatSessionTitle } from '../llmChatSessionLabels'
import {
  continueAfterCatalogDismissStream,
  continueAfterCatalogSaveStream,
} from '../consumeLlmChatUiMessageStream'
import {
  selectSession,
  selectSessionMessages,
  selectSessionState,
  submitFeedbackViaStore,
  type StreamingStatus,
  useLlmChatStore,
} from '../store/llmChatStore'
import { LlmChatComposer } from './LlmChatComposer'
import { LlmChatSessionScopeHeader } from './LlmChatSessionScopeHeader'
import { LlmChatMessageBubble } from './LlmChatMessageBubble'
import {
  buildCitationResolutionIndex,
  resolveCitationTargetFromHrefOrRadar,
} from './citationTargetResolver'
import { type LegislationCitationTarget } from './llmChatToolOutput'

function extractLastUserTextFromUIMessages(messages: UIMessage[]): string {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const messageItem = messages[index]
    if (messageItem.role !== 'user') {
      continue
    }
    let combined = ''
    for (const part of messageItem.parts) {
      if (part.type === 'text' && typeof part.text === 'string') {
        combined += part.text
      }
    }
    const trimmed = combined.trim()
    if (trimmed.length > 0) {
      return trimmed
    }
  }
  return ''
}

type LlmChatSessionThreadProps = {
  sessionId: string
  onOpenCitation: (target: LegislationCitationTarget) => void
}

export function LlmChatSessionThread({
  sessionId,
  onOpenCitation,
}: LlmChatSessionThreadProps) {
  const queryClient = useQueryClient()
  const lastSyncedServerStamp = useRef<number>(0)
  const { t } = useTranslation('llmChat')

  const sessionState = useLlmChatStore((state) =>
    selectSessionState(state, sessionId)
  )
  const session = useLlmChatStore((state) => selectSession(state, sessionId))
  const persistedMessages = useLlmChatStore((state) =>
    selectSessionMessages(state, sessionId)
  )
  const feedbackState = useLlmChatStore((state) => state.feedback)
  const streamError = useLlmChatStore(
    (state) => state.streaming.get(sessionId)?.error
  )
  const selectedModel = useLlmChatStore((state) =>
    state.getSessionModel(sessionId)
  )
  const setSessionModel = useLlmChatStore((state) => state.setSessionModel)
  const getAndClearBootstrapMessage = useLlmChatStore(
    (state) => state.getAndClearBootstrapMessage
  )
  const getAndClearBootstrapModel = useLlmChatStore(
    (state) => state.getAndClearBootstrapModel
  )
  const setSessionFromSync = useLlmChatStore((state) => state.setSessionFromSync)
  const setProductRecommendations = useLlmChatStore(
    (state) => state.setProductRecommendations
  )
  const handleStreamError = useLlmChatStore((state) => state.handleStreamError)
  const clearStreamError = useLlmChatStore((state) => state.clearStreamError)
  const setStreamingStatus = useLlmChatStore((state) => state.setStreamingStatus)
  const setRecommendationGatePending = useLlmChatStore(
    (state) => state.setRecommendationGatePending
  )
  const clearRecommendationGatePending = useLlmChatStore(
    (state) => state.clearRecommendationGatePending
  )

  const providerInfoQuery = useLlmChatProviderInfo()
  const linkedProductStatus = useSessionLinkedProductStatus(
    session,
    persistedMessages
  )
  const sessionScope = useLlmChatSessionScopeLabel(session?.productId)
  const chatTitle = useMemo(() => {
    if (session === undefined || session === null) {
      return t('untitled')
    }
    return llmChatSessionTitle(session, t('untitled'))
  }, [session, t])
  const chatModelOptions = providerInfoQuery.data?.models.chatModelOptions ?? []
  const defaultChatModelId = providerInfoQuery.data?.models.stateOfTheArtChat
  const selectedModelRef = useRef<string | undefined>(undefined)

  const displayModelForSelect =
    selectedModel ??
    defaultChatModelId ??
    (chatModelOptions.length > 0 ? chatModelOptions[0] : undefined)

  useEffect(() => {
    selectedModelRef.current = selectedModel
  }, [selectedModel])

  const handleModelChange = useCallback(
    (model: string) => {
      setSessionModel(sessionId, model)
    },
    [sessionId, setSessionModel]
  )

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_BASE_URL ?? ''}/v1/llm-chat/sessions/${sessionId}/messages/stream`,
        credentials: 'include',
        headers: () => ({
          'Accept-Language': i18n.language,
        }),
        prepareSendMessagesRequest: ({ messages }) => {
          const body: { content: string; model?: string } = {
            content: extractLastUserTextFromUIMessages(messages).trim(),
          }
          const model = selectedModelRef.current
          if (model !== undefined && model !== '') {
            body.model = model
          }
          return { body }
        },
      }),
    [sessionId]
  )

  const chatHandlesRef = useRef<UseChatHelpers<UIMessage> | null>(null)
  const [isContinueStreamBusy, setIsContinueStreamBusy] = useState(false)

  const chat = useChat<UIMessage>({
    id: sessionId,
    transport,
    experimental_throttle: 50,
    onData: (dataPart) => {
      const partKind = dataPart.type
      if (partKind === 'data-lex-llm-chat-session-sync') {
        const snapshot = dataPart.data as Pick<
          GetLlmChatSessionResponse,
          'session' | 'messages'
        >
        if (
          snapshot.session !== null &&
          snapshot.session !== undefined &&
          snapshot.messages !== undefined
        ) {
          setSessionFromSync(sessionId, snapshot)
          patchLlmChatSessionInListCache(queryClient, snapshot.session)
        }
        return
      }
      if (partKind === 'data-lex-llm-chat-user-message-ack') {
        const ack = dataPart.data as { userMessageId: string }
        chatHandlesRef.current?.setMessages((previous) => {
          if (previous.length === 0) {
            return previous
          }
          const lastIdx = previous.length - 1
          const tail = previous[lastIdx]
          if (tail.role !== 'user') {
            return previous
          }
          if (tail.id === ack.userMessageId) {
            return previous
          }
          return [
            ...previous.slice(0, lastIdx),
            { ...tail, id: ack.userMessageId },
          ]
        })
      }
      if (partKind === 'data-lex-product-recommendations-sync') {
        const syncData = dataPart.data as {
          productId: string
          sessionId: string
          recommendations: ProductRecommendation[]
        }
        setProductRecommendations(
          syncData.productId,
          syncData.sessionId,
          syncData.recommendations
        )
        setProductRecommendationsCache(
          queryClient,
          syncData.productId,
          syncData.sessionId,
          syncData.recommendations
        )
      }
      if (partKind === 'data-lex-llm-chat-stream-error') {
        const errorData = dataPart.data as {
          message: string
          retryable: boolean
          userMessageId: string | null
        }
        handleStreamError(sessionId, errorData)
      }
      if (partKind === 'data-lex-llm-chat-recommendation-gate-pending') {
        setRecommendationGatePending(sessionId, true)
      }
    },
    onFinish: () => {
      invalidateLlmChatSessionsList(queryClient)
      clearStreamError(sessionId)
      clearRecommendationGatePending(sessionId)
    },
    onError: () => {
      setStreamingStatus(sessionId, 'error')
    },
  })

  chatHandlesRef.current = chat

  useEffect(() => {
    const mappedStatus: StreamingStatus =
      chat.status === 'ready' ? 'idle' : chat.status
    setStreamingStatus(sessionId, mappedStatus)
  }, [chat.status, sessionId, setStreamingStatus])

  useLayoutEffect(() => {
    if (!providerInfoQuery.isFetched) {
      setSessionModel(sessionId, undefined)
      selectedModelRef.current = undefined
      return
    }
    const pendingModel = getAndClearBootstrapModel(sessionId)
    if (pendingModel !== null && pendingModel !== '') {
      setSessionModel(sessionId, pendingModel)
      selectedModelRef.current = pendingModel
    } else {
      setSessionModel(sessionId, undefined)
      selectedModelRef.current = undefined
    }
    const pending = getAndClearBootstrapMessage(sessionId)
    if (!pending) {
      return
    }
    clearStreamError(sessionId)
    void chatHandlesRef.current?.sendMessage({ text: pending })
  }, [
    sessionId,
    providerInfoQuery.isFetched,
    getAndClearBootstrapMessage,
    getAndClearBootstrapModel,
    setSessionModel,
    clearStreamError,
  ])

  useEffect(() => {
    if (!sessionState || sessionState.loadStatus !== 'loaded') {
      return
    }
    const handlesForSync = chatHandlesRef.current
    if (!handlesForSync) {
      return
    }
    if (
      handlesForSync.status === 'streaming' ||
      handlesForSync.status === 'submitted'
    ) {
      return
    }
    if (lastSyncedServerStamp.current === sessionState.serverSyncStamp) {
      return
    }
    lastSyncedServerStamp.current = sessionState.serverSyncStamp
    handlesForSync.setMessages(llmChatRowsToUiMessages(sessionState.messages))
  }, [sessionState])

  const viewportRef = useRef<HTMLDivElement>(null)

  const isSubmitted = chat.status === 'submitted'
  const isStreaming = chat.status === 'streaming'
  const isAssistantResponseInProgress = isSubmitted || isStreaming

  const messages = chat.messages
  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : undefined
  const lastMessageIsUser = lastMessage?.role === 'user'
  const shouldShowAssistantPlaceholderWhileAwaitingAssistantRow =
    isAssistantResponseInProgress &&
    (messages.length === 0 || lastMessageIsUser)

  const showEmptyThreadHint =
    messages.length === 0 && !isAssistantResponseInProgress

  const userScrolledUpRef = useRef(false)
  const hasScrolledToBottomOnceRef = useRef(false)
  const SCROLL_BOTTOM_THRESHOLD = 80

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = viewportRef.current
    if (!el) {
      return
    }
    el.scrollTo({ top: el.scrollHeight, behavior })
  }, [])

  const handleViewportScroll = useCallback(() => {
    const el = viewportRef.current
    if (!el) {
      return
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    userScrolledUpRef.current = distanceFromBottom > SCROLL_BOTTOM_THRESHOLD
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) {
      return
    }
    el.addEventListener('scroll', handleViewportScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', handleViewportScroll)
    }
  }, [handleViewportScroll])

  useLayoutEffect(() => {
    const hasThreadContent =
      messages.length > 0 ||
      shouldShowAssistantPlaceholderWhileAwaitingAssistantRow
    if (!hasThreadContent) {
      return
    }
    if (userScrolledUpRef.current) {
      return
    }
    if (!hasScrolledToBottomOnceRef.current) {
      hasScrolledToBottomOnceRef.current = true
      scrollToBottom('auto')
      return
    }
    scrollToBottom(isAssistantResponseInProgress ? 'auto' : 'smooth')
  }, [
    messages,
    shouldShowAssistantPlaceholderWhileAwaitingAssistantRow,
    isAssistantResponseInProgress,
    scrollToBottom,
  ])

  const handleSend = useCallback(
    (text: string) => {
      const handles = chatHandlesRef.current
      if (!handles) {
        return
      }
      if (
        !text ||
        handles.status === 'streaming' ||
        handles.status === 'submitted'
      ) {
        return
      }
      clearStreamError(sessionId)
      clearRecommendationGatePending(sessionId)
      void handles.sendMessage({ text })
      userScrolledUpRef.current = false
      scrollToBottom('auto')
    },
    [clearRecommendationGatePending, clearStreamError, scrollToBottom, sessionId]
  )

  const handleRetryStream = useCallback(() => {
    const lastUserText = extractLastUserTextFromUIMessages(messages)
    if (lastUserText.length === 0) {
      return
    }
    clearStreamError(sessionId)
    handleSend(lastUserText)
  }, [clearStreamError, handleSend, messages, sessionId])

  const runCatalogResolveAndContinue = useCallback(
    async (
      recommendationId: string,
      resolution: 'accepted' | 'dismissed'
    ) => {
      if (isContinueStreamBusy) {
        return
      }
      const handles = chatHandlesRef.current
      if (handles === null) {
        toast.error('Chat session is not ready to continue')
        return
      }
      // Continue is a separate fetch; stop any leftover useChat transport so it
      // cannot fight message updates. Do not poll for idle — server owns resolve.
      if (
        handles.status === 'streaming' ||
        handles.status === 'submitted'
      ) {
        handles.stop()
      }

      const productId = session?.productId
      if (productId) {
        patchRecommendationStatusInCache(
          queryClient,
          productId,
          sessionId,
          recommendationId,
          resolution
        )
      }

      setIsContinueStreamBusy(true)
      clearRecommendationGatePending(sessionId)
      setStreamingStatus(sessionId, 'submitted')
      try {
        if (resolution === 'accepted') {
          await continueAfterCatalogSaveStream({
            sessionId,
            recommendationId,
            model: selectedModelRef.current,
            queryClient,
            setMessages: handles.setMessages,
          })
        } else {
          await continueAfterCatalogDismissStream({
            sessionId,
            recommendationId,
            model: selectedModelRef.current,
            queryClient,
            setMessages: handles.setMessages,
          })
        }
        invalidateLlmChatSessionsList(queryClient)
        if (productId) {
          invalidateProductRecommendationQueries(
            queryClient,
            productId,
            sessionId
          )
        }
        clearRecommendationGatePending(sessionId)
        setStreamingStatus(sessionId, 'idle')
      } catch (error) {
        setStreamingStatus(sessionId, 'error')
        if (productId) {
          invalidateProductRecommendationQueries(
            queryClient,
            productId,
            sessionId
          )
        }
        const message =
          error instanceof Error ? error.message : 'Failed to continue chat'
        toast.error(message)
      } finally {
        setIsContinueStreamBusy(false)
      }
    },
    [
      clearRecommendationGatePending,
      isContinueStreamBusy,
      queryClient,
      session?.productId,
      sessionId,
      setStreamingStatus,
    ]
  )

  const handleContinueAfterCatalogSave = useCallback(
    (recommendationId: string) => {
      void runCatalogResolveAndContinue(recommendationId, 'accepted')
    },
    [runCatalogResolveAndContinue]
  )

  const handleContinueAfterCatalogDismiss = useCallback(
    (recommendationId: string) => {
      void runCatalogResolveAndContinue(recommendationId, 'dismissed')
    },
    [runCatalogResolveAndContinue]
  )

  const isComposerBusy =
    chat.status === 'streaming' || chat.status === 'submitted'
  const isChatStreamBusy = isComposerBusy || isContinueStreamBusy

  const feedbackEligibleAssistantMessageIds = useMemo(
    () => getFeedbackEligibleAssistantMessageIds(messages),
    [messages]
  )

  const messageRows = useMemo(
    () =>
      !showEmptyThreadHint
        ? messages.map((message, index) => {
            const isLastMessageInThread = index === messages.length - 1
            const isTargetAssistantMessage =
              message.role === 'assistant' && isLastMessageInThread
            const isActiveAssistantTurn =
              isTargetAssistantMessage && isAssistantResponseInProgress
            const isAwaitingAssistantFirstChunk =
              isTargetAssistantMessage && isSubmitted
            const isAssistantResponseStreaming =
              isTargetAssistantMessage && isStreaming
            const isLastAssistantMessageInRun =
              message.role === 'assistant' &&
              feedbackEligibleAssistantMessageIds.has(message.id)
            return {
              message,
              isActiveAssistantTurn,
              isAwaitingAssistantFirstChunk,
              isAssistantResponseStreaming,
              isLastAssistantMessageInRun,
            }
          })
        : [],
    [
      showEmptyThreadHint,
      messages,
      isAssistantResponseInProgress,
      isSubmitted,
      isStreaming,
      feedbackEligibleAssistantMessageIds,
    ]
  )

  const feedbackByMessageId = useMemo(() => {
    const map = new Map<string, LlmChatMessageFeedbackDto>()
    for (const [messageId, entry] of feedbackState) {
      if (entry.feedback) {
        map.set(messageId, entry.feedback)
      }
    }
    return map
  }, [feedbackState])

  const feedbackPendingByMessageId = useMemo(() => {
    const map = new Map<string, boolean>()
    for (const [messageId, entry] of feedbackState) {
      if (entry.pending) {
        map.set(messageId, true)
      }
    }
    return map
  }, [feedbackState])

  const persistedMessagesById = useMemo(() => {
    const map = new Map<string, (typeof persistedMessages)[number]>()
    for (const message of persistedMessages) {
      map.set(message.id, message)
    }
    return map
  }, [persistedMessages])

  const citationResolutionIndex = useMemo(
    () => buildCitationResolutionIndex(messages),
    [messages]
  )

  const showLinkedProductDeletedAlert = linkedProductStatus.linkedProductDeleted
  const showLinkedProductUpdatedAlert =
    !showLinkedProductDeletedAlert &&
    !isAssistantResponseInProgress &&
    linkedProductStatus.linkedProductUpdatedAfterLastMessage
  const showLinkedProductAlert =
    showLinkedProductDeletedAlert || showLinkedProductUpdatedAlert
  const showSessionHeader = session !== undefined && session !== null
  const showStreamError = streamError !== undefined

  const handleSubmitFeedback = useCallback(
    async (
      messageId: string,
      payload: SubmitLlmChatMessageFeedbackPayload
    ) => {
      await submitFeedbackViaStore({
        sessionId,
        messageId,
        payload,
        submitApi: submitLlmChatMessageFeedbackApi,
      })
      invalidateLlmChatSessionsList(queryClient)
    },
    [queryClient, sessionId]
  )

  return (
    <>
      {showSessionHeader ? (
        <LlmChatSessionScopeHeader
          title={chatTitle}
          scope={sessionScope.scope}
          productId={session?.productId}
          isScopeLoading={sessionScope.isScopeLoading}
        />
      ) : null}
      <div className='relative min-h-0 flex-1'>
        {showLinkedProductAlert ? (
          <div className='absolute inset-x-3 top-3 z-10'>
            <div className='mx-auto w-full max-w-3xl'>
              <Alert
                variant='warning'
                className='bg-amber-50 shadow-sm dark:bg-yellow-900'
              >
                <AlertTitle>
                  {showLinkedProductDeletedAlert
                    ? t('linkedProductDeleted.title')
                    : t('linkedProductUpdated.title')}
                </AlertTitle>
                <AlertDescription>
                  {showLinkedProductDeletedAlert
                    ? t('linkedProductDeleted.description')
                    : t('linkedProductUpdated.description')}
                </AlertDescription>
              </Alert>
            </div>
          </div>
        ) : null}
        {showStreamError ? (
          <div
            className={`absolute inset-x-3 z-10 ${
              showLinkedProductAlert ? 'top-28 sm:top-24' : 'top-3'
            }`}
          >
            <div className='mx-auto w-full max-w-3xl'>
              <Alert variant='destructive' className='shadow-sm'>
                <AlertTitle>{t('streamError.title')}</AlertTitle>
                <AlertDescription className='flex flex-col gap-2'>
                  <span>{streamError.message}</span>
                  {streamError.retryable ? (
                    <Button
                      type='button'
                      size='sm'
                      variant='secondary'
                      onClick={handleRetryStream}
                      disabled={isComposerBusy}
                    >
                      {t('streamError.retry')}
                    </Button>
                  ) : null}
                </AlertDescription>
              </Alert>
            </div>
          </div>
        ) : null}
        <ScrollArea className='h-full' viewportRef={viewportRef}>
          <div
            className={`mx-auto flex w-full max-w-3xl flex-col px-3 ${
              showLinkedProductAlert || showStreamError ? 'pt-28 sm:pt-24' : ''
            }`}
          >
            {showEmptyThreadHint && (
              <p className='py-12 text-center text-[13px] text-muted-foreground'>
                {t('emptyThread')}
              </p>
            )}
            <div className='space-y-2 pb-4'>
              {messageRows.map(
                ({
                  message,
                  isActiveAssistantTurn,
                  isAwaitingAssistantFirstChunk,
                  isAssistantResponseStreaming,
                  isLastAssistantMessageInRun,
                }) => (
                  <LlmChatMessageBubble
                    key={message.id}
                    message={message}
                    persistedMessage={persistedMessagesById.get(message.id)}
                    sessionId={sessionId}
                    sessionProductId={session?.productId}
                    isActiveAssistantTurn={isActiveAssistantTurn}
                    isAwaitingAssistantFirstChunk={
                      isAwaitingAssistantFirstChunk
                    }
                    isAssistantResponseStreaming={isAssistantResponseStreaming}
                    isLastAssistantMessageInRun={isLastAssistantMessageInRun}
                    existingFeedback={feedbackByMessageId.get(message.id)}
                    isFeedbackSubmitting={
                      feedbackPendingByMessageId.get(message.id) ?? false
                    }
                    onSubmitFeedback={handleSubmitFeedback}
                    onOpenCitationLink={async (input) => {
                      const resolvedTarget =
                        await resolveCitationTargetFromHrefOrRadar({
                          href: input.href,
                          label: input.label,
                          sourceMessageId: message.id,
                          index: citationResolutionIndex,
                          lookupByCelex: fetchLegislationDocumentByCelex,
                        })
                      if (!resolvedTarget) {
                        return false
                      }
                      onOpenCitation(resolvedTarget)
                      return true
                    }}
                    onOpenCitation={onOpenCitation}
                    isChatStreamBusy={isChatStreamBusy}
                    onContinueAfterCatalogSave={handleContinueAfterCatalogSave}
                    onContinueAfterCatalogDismiss={
                      handleContinueAfterCatalogDismiss
                    }
                  />
                )
              )}
            </div>
            {shouldShowAssistantPlaceholderWhileAwaitingAssistantRow ? (
              <div className='flex w-full justify-start'>
                <div className='flex max-w-[min(100%,42rem)] items-center gap-2 text-[13px] text-muted-foreground'>
                  <Loader2
                    className='h-3.5 w-3.5 shrink-0 animate-spin'
                    aria-hidden
                  />
                  <span>{t('assistantReplyPending')}</span>
                </div>
              </div>
            ) : null}
          </div>
        </ScrollArea>
      </div>

      <LlmChatComposer
        key={sessionId}
        onSend={handleSend}
        isSending={isComposerBusy}
        canStopGenerating={isComposerBusy}
        onStopGenerating={() => {
          chat.stop()
        }}
        modelOptions={chatModelOptions}
        selectedModel={displayModelForSelect}
        onModelChange={handleModelChange}
      />
    </>
  )
}
